// =========================================================================
// TON-MILE SUPPORT CALCULATORS (Batch F2 — F2a)
// Transparent, conservative, documentation-only estimates of drilling-line
// ton-mile accumulation. These calculators do NOT replace contractor
// slip-and-cut programs, drilling-line records, API guidance, rig-specific
// reeving factors, or company procedures. Number-of-lines-strung is
// recorded as documentation only; the simplified estimate does not divide
// by it (contractor-specific reeving factors must be applied separately).
// =========================================================================
import { round, num, positive } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive };

// One ton-mile = 2,000 lb × 5,280 ft = 10,560,000 lb-ft.
const LB_FT_PER_TON_MILE = 2000 * 5280;

const TON_MILE_DISCLAIMERS = [
  "Documentation support only. This is a simplified ton-mile estimate and does not replace contractor slip-and-cut programs, drilling-line records, API guidance, rig-specific reeving factors, or company procedures.",
  "Number-of-lines-strung is recorded for documentation. The simplified estimate does not divide by it; contractor-specific reeving and correction factors must be applied separately per company procedure.",
];

function _normalisedFactor(raw) {
  if (raw === "" || raw === null || raw === undefined) return 1;
  const f = Number(raw);
  if (!Number.isFinite(f)) throw new Error("Invalid number for Correction Factor");
  if (f <= 0) throw new Error("Correction Factor must be greater than zero");
  return f;
}

function _optionalNonNegative(raw, name) {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) throw new Error(`Invalid number for ${name}`);
  if (n < 0) throw new Error(`${name} must be ≥ 0`);
  return n;
}

function _linesStrungInt(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0) throw new Error("Number of Lines Strung must be a positive integer");
  return n;
}

// ----- F2a-A: Round Trip Ton Miles ---------------------------------------
// Distance travelled in a round trip = 2 × trip_depth (out and back in).
// Effective load = hookload + block_weight_allowance.
// TM = (W_eff × distance × correction) / 10,560,000
export function roundTripTonMiles({
  hookload, tripDepth, blockWeightAllowance, linesStrung, correctionFactor, operationNote,
}) {
  const W = positive(num(hookload, "Hookload / String Weight"), "Hookload / String Weight");
  const D = positive(num(tripDepth, "Trip Depth"), "Trip Depth");
  const Bw = _optionalNonNegative(blockWeightAllowance, "Block Weight Allowance");
  const Ls = _linesStrungInt(linesStrung);
  const f = _normalisedFactor(correctionFactor);
  const Weff = W + Bw;
  const distance = 2 * D;
  const tm = (Weff * distance * f) / LB_FT_PER_TON_MILE;
  return {
    result: round(tm, 2),
    unit: "ton-miles",
    formula: "TM_round = ((Hookload + Block_weight) × 2 × Trip_depth × Correction_factor) / 10,560,000",
    steps: [
      "Round Trip Ton Miles — Simplified Support Estimate",
      `Effective load = ${W} + ${Bw} = ${round(Weff, 0)} lbf`,
      `Round-trip distance = 2 × ${D} = ${round(distance, 0)} ft`,
      `TM = (${round(Weff, 0)} × ${round(distance, 0)} × ${f}) / 10,560,000 = ${round(tm, 2)} ton-miles`,
      Ls === null ? "Lines strung: not entered (documentation only)." : `Lines strung: ${Ls} (recorded for documentation only).`,
      operationNote ? `Operation note: ${String(operationNote)}` : "Operation note: not entered.",
      ...TON_MILE_DISCLAIMERS,
    ],
    outputs: {
      "Estimated round-trip ton-miles": round(tm, 2),
      "Effective load (lbf)": round(Weff, 0),
      "Round-trip distance (ft)": round(distance, 0),
    },
    inputs: {
      "Hookload (lbf)": W, "Trip Depth (ft)": D, "Block Weight Allowance (lbf)": Bw,
      "Lines Strung": Ls === null ? "Not entered" : Ls,
      "Correction Factor": f, "Operation note": operationNote || "Not entered",
    },
  };
}

// ----- F2a-B: Single Trip / One-Way Ton Miles ----------------------------
// TM = (W_eff × movement_distance × correction) / 10,560,000
export function singleTripTonMiles({
  hookload, movementDistance, blockWeightAllowance, linesStrung, correctionFactor,
}) {
  const W = positive(num(hookload, "Hookload / String Weight"), "Hookload / String Weight");
  const D = positive(num(movementDistance, "Movement Distance"), "Movement Distance");
  const Bw = _optionalNonNegative(blockWeightAllowance, "Block Weight Allowance");
  const Ls = _linesStrungInt(linesStrung);
  const f = _normalisedFactor(correctionFactor);
  const Weff = W + Bw;
  const tm = (Weff * D * f) / LB_FT_PER_TON_MILE;
  return {
    result: round(tm, 2),
    unit: "ton-miles",
    formula: "TM_one_way = ((Hookload + Block_weight) × Movement_distance × Correction_factor) / 10,560,000",
    steps: [
      "Single-Trip / One-Way Ton Miles — Simplified Support Estimate",
      `Effective load = ${W} + ${Bw} = ${round(Weff, 0)} lbf`,
      `TM = (${round(Weff, 0)} × ${D} × ${f}) / 10,560,000 = ${round(tm, 2)} ton-miles`,
      Ls === null ? "Lines strung: not entered (documentation only)." : `Lines strung: ${Ls} (recorded for documentation only).`,
      ...TON_MILE_DISCLAIMERS,
    ],
    outputs: {
      "Estimated one-way ton-miles": round(tm, 2),
      "Effective load (lbf)": round(Weff, 0),
      "Movement distance (ft)": D,
    },
    inputs: {
      "Hookload (lbf)": W, "Movement Distance (ft)": D, "Block Weight Allowance (lbf)": Bw,
      "Lines Strung": Ls === null ? "Not entered" : Ls,
      "Correction Factor": f,
    },
  };
}

// ----- F2a-C: Cumulative Ton Miles Since Slip-and-Cut --------------------
// new_cumulative = current_cumulative + new_operation_tm
// If a planning threshold is entered, also report remaining and % used.
// Non-verdict caution note if cumulative exceeds the entered threshold.
export function cumulativeTonMiles({
  currentCumulative, newOperationTM, slipAndCutThreshold, notes,
}) {
  const C = _optionalNonNegative(currentCumulative, "Current Accumulated Ton-Miles");
  const N = _optionalNonNegative(newOperationTM, "New Operation Ton-Miles");
  const total = C + N;
  const tRaw = slipAndCutThreshold;
  const hasThreshold = tRaw !== "" && tRaw !== null && tRaw !== undefined && !Number.isNaN(Number(tRaw)) && Number(tRaw) > 0;
  const T = hasThreshold ? Number(tRaw) : null;
  const remaining = hasThreshold ? T - total : null;
  const pctUsed = hasThreshold ? (total / T) * 100 : null;
  const exceeded = hasThreshold && total > T;

  const steps = [
    "Cumulative Ton Miles Since Slip-and-Cut — Documentation Support",
    `Current accumulated ton-miles = ${round(C, 2)}`,
    `New operation ton-miles = ${round(N, 2)}`,
    `New cumulative = ${round(C, 2)} + ${round(N, 2)} = ${round(total, 2)} ton-miles`,
  ];
  if (hasThreshold) {
    steps.push(`Entered planning threshold = ${round(T, 2)} ton-miles`);
    steps.push(`Remaining to entered threshold = ${round(T, 2)} − ${round(total, 2)} = ${round(remaining, 2)} ton-miles`);
    steps.push(`Threshold used = ${round(pctUsed, 1)} %`);
    if (exceeded) {
      steps.push("Entered planning threshold has been exceeded. Review line program, rig procedure, and supervisor instructions.");
    }
  } else {
    steps.push("Planning threshold not entered. Remaining-to-threshold and percent-used not calculated.");
  }
  if (notes) steps.push(`Notes / procedure reference: ${String(notes)}`);
  steps.push(...TON_MILE_DISCLAIMERS);

  return {
    result: round(total, 2),
    unit: "ton-miles (cumulative)",
    formula: "New_cumulative = Current_cumulative + New_operation_TM ;  Remaining = Threshold − New_cumulative",
    steps,
    outputs: {
      "New cumulative ton-miles": round(total, 2),
      "Remaining to entered threshold (ton-miles)": hasThreshold ? round(remaining, 2) : "Not calculated — threshold not entered",
      "Threshold used (%)": hasThreshold ? round(pctUsed, 1) : "Not calculated — threshold not entered",
      "Threshold-exceeded note": exceeded ? "Entered planning threshold has been exceeded. Review line program, rig procedure, and supervisor instructions." : "Within entered threshold or threshold not entered.",
    },
    inputs: {
      "Current Accumulated Ton-Miles": C, "New Operation Ton-Miles": N,
      "Entered Planning Threshold (ton-miles)": hasThreshold ? T : "Not entered",
      "Notes": notes || "Not entered",
    },
  };
}
